import { createContext, useContext, useState, useCallback } from "react";

const translations = {
  en: {
    // Navigation
    dashboard: "Dashboard",
    systemPulse: "System Pulse",
    crm: "CRM",
    clients: "Clients",
    events: "Events",
    calendar: "Calendar",
    recipes: "Recipes",
    inventory: "Inventory",
    documents: "Documents",
    settings: "Settings",
    logout: "Logout",
    executionHub: "Execution Hub",
    
    // Common
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    filter: "Filter",
    actions: "Actions",
    status: "Status",
    name: "Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    notes: "Notes",
    date: "Date",
    total: "Total",
    loading: "Loading...",
    noResults: "No results found",
    duplicate: "Duplicate",
    booking: "Booking",
    viewAll: "View all",
    add: "Add",
    record: "Record",
    
    // Status labels
    planning: "Planning",
    inProgress: "In Progress",
    blocked: "Blocked",
    completed: "Completed",
    cancelled: "Cancelled",
    confirmed: "Confirmed",
    live: "Live",
    archived: "Archived",
    pending: "Pending",
    resolved: "Resolved",
    active: "Active",
    inactive: "Inactive",
    
    // Execution Hub
    executionGoals: "Execution Goals",
    newGoal: "New Goal",
    createGoal: "Create Goal",
    goalTitle: "Title",
    goalObjective: "Objective",
    targetDate: "Target Date",
    impactAreas: "Impact Areas",
    context: "Context",
    organizational: "Organizational",
    event: "Event",
    department: "Department",
    personal: "Personal",
    cost: "Cost",
    quality: "Quality",
    speed: "Speed",
    risk: "Risk",
    tasks: "Tasks",
    decisions: "Decisions",
    dependencies: "Dependencies",
    decisionLog: "Decision Log",
    newTask: "New Task",
    createTask: "Create Task",
    taskTitle: "Task Title",
    taskDescription: "Description",
    dueDate: "Due Date",
    recordDecision: "Record Decision",
    decisionContent: "Decision",
    rationale: "Rationale",
    newDependency: "New Dependency",
    addDependency: "Add Dependency",
    goalNotes: "Goal Notes",
    taskNotes: "Task Notes",
    addNote: "Add note...",
    noTasksYet: "No tasks yet",
    noDecisionsRecorded: "No decisions recorded",
    noDependencies: "No dependencies",
    noNotesYet: "No notes yet",
    noGoalsYet: "No goals yet",
    createFirstGoal: "Create your first execution goal to get started",
    goalsThisMonth: "goals this month",
    activeGoals: "Active Goals",
    blockedGoals: "Blocked",
    completedGoals: "Completed",
    thisMonth: "This Month",
    searchGoals: "Search goals...",
    filterByStatus: "Filter by status",
    all: "All",
    changeStatus: "Change Status",
    deleteGoal: "Delete Goal",
    confirmDeleteGoal: "Are you sure you want to delete this goal?",
    goalDeleted: "Goal deleted",
    goalCreated: "Goal created successfully",
    taskCreated: "Task created",
    taskUpdated: "Task updated",
    decisionRecorded: "Decision recorded",
    dependencyAdded: "Dependency added",
    dependencyUpdated: "Dependency updated",
    noteAdded: "Note added",
    statusUpdated: "Status updated",
    titleObjectiveRequired: "Title and objective are required",
    errorLoadingGoals: "Error loading goals",
    errorLoadingGoal: "Error loading goal",
    errorCreatingGoal: "Error creating goal",
    errorCreatingTask: "Error creating task",
    errorUpdating: "Error updating",
    errorDeleting: "Error deleting",
    errorAddingNote: "Error adding note",
    errorRecordingDecision: "Error recording decision",
    errorAddingDependency: "Error adding dependency",
    newExecutionGoal: "New Execution Goal",
    defineNewGoal: "Define a new operational goal for your organization",
    manageGoalsTrackProgress: "Manage your operational goals and track progress",
    assignedGoalsAndTasks: "Assigned goals and tasks",
    myPending: "My Pending",
    myGoals: "My Goals",
    assignedTasks: "Assigned Tasks",
    
    // System Pulse / Signals
    executionSignals: "Execution Signals",
    viewAllGoals: "View all goals",
    goalBlocked: "Goal Blocked",
    goalAtRisk: "Goal At Risk",
    goalCompleted: "Goal Completed",
    isBlocked: "is blocked",
    hasUpcomingDeadline: "has upcoming deadline",
    wasCompletedThisWeek: "was completed this week",
    atRisk: "at risk",
    completedThisWeek: "completed this week",
    
    // Auth
    login: "Login",
    register: "Register",
    password: "Password",
    confirmPassword: "Confirm Password",
    companyName: "Company Name",
    welcomeBack: "Welcome back",
    createAccount: "Create your account",
    continueWithGoogle: "Continue with Google",
    orContinueWith: "or continue with",
    
    // Dashboard
    welcomeDashboard: "Welcome to your dashboard",
    totalClients: "Total Clients",
    upcomingEvents: "Upcoming Events",
    totalRecipes: "Total Recipes",
    lowStockAlerts: "Low Stock Alerts",
    recentEvents: "Recent Events",
    salesPipeline: "Sales Pipeline",
    
    // CRM
    addClient: "Add Client",
    clientDetails: "Client Details",
    pipelineStage: "Pipeline Stage",
    totalRevenue: "Total Revenue",
    eventsCount: "Events",
    lead: "Lead",
    contacted: "Contacted",
    proposal: "Proposal",
    negotiation: "Negotiation",
    won: "Won",
    lost: "Lost",
    
    // Events
    addEvent: "Add Event",
    eventDetails: "Event Details",
    eventType: "Event Type",
    eventDate: "Event Date",
    venue: "Venue",
    guestCount: "Guest Count",
    budget: "Budget",
    timeline: "Timeline",
    menu: "Menu",
    
    // Recipes
    addRecipe: "Add Recipe",
    recipeDetails: "Recipe Details",
    category: "Category",
    servings: "Servings",
    prepTime: "Prep Time",
    cookTime: "Cook Time",
    ingredients: "Ingredients",
    instructions: "Instructions",
    totalCost: "Total Cost",
    costPerServing: "Cost per Serving",
    aiAssistant: "AI Assistant",
    analyzeRecipe: "Analyze Recipe",
    
    // Inventory
    addIngredient: "Add Ingredient",
    ingredientDetails: "Ingredient Details",
    unit: "Unit",
    costPerUnit: "Cost per Unit",
    stockQuantity: "Stock Quantity",
    minStockLevel: "Min Stock Level",
    supplier: "Supplier",
    suppliers: "Suppliers",
    addSupplier: "Add Supplier",
    lowStock: "Low Stock",
    
    // Documents
    createDocument: "Create Document",
    quote: "Proposal",
    contract: "Contract",
    invoice: "Invoice",
    beo: "Banquet Event Order",
    kitchenSheet: "Kitchen Sheet",
    packingList: "Packing/Setup List",
    miseEnPlace: "Mise en Place",
    documentType: "Document Type",
    sign: "Sign",
    signed: "Signed",
    draft: "Draft",
    sent: "Sent",
    notSigned: "Not Signed",
    
    // Settings
    companySettings: "Company Settings",
    branding: "Branding",
    templates: "Templates",
    documentTemplates: "Document Templates",
    users: "Users",
    language: "Language",
    currency: "Currency",
    primaryColor: "Primary Color",
    secondaryColor: "Secondary Color",
    logoUrl: "Logo URL",
    showPrices: "Show Prices",
    requiresSignature: "Requires Signature",
    
    // Portal
    clientPortal: "Client Portal",
    signDocument: "Sign Document",
    thankYou: "Thank you for your signature!",
    
    // Locations & Venues
    locations: "Locations",
    venues: "Venues",
    rooms: "Rooms",
    addVenue: "Add Venue",
    editVenue: "Edit Venue",
    addRoom: "Add Room",
    editRoom: "Edit Room",
    venueName: "Venue Name",
    roomName: "Room Name",
    capacity: "Capacity",
    floor: "Floor",
    amenities: "Amenities",
    setupOptions: "Setup Options",
    hourlyRate: "Hourly Rate",
    parkingInfo: "Parking Info",
    accessibilityInfo: "Accessibility",
    isPrimary: "Primary Location",
    savedAddresses: "Saved Addresses",
    addAddress: "Add Address",
    locationType: "Location Type",
    onsite: "Onsite",
    offsite: "Offsite",
    clientAddress: "Client Address",
    multiLocation: "Multiple Locations",
    selectVenue: "Select Venue",
    selectRoom: "Select Room",
    arrivalTime: "Arrival Time",
    departureTime: "Departure Time",
    setupNotes: "Setup Notes",
    parkingInstructions: "Parking Instructions",
    noVenuesYet: "No venues yet",
    createFirstVenue: "Create your first venue to get started",
    noRoomsYet: "No rooms in this venue",
    addFirstRoom: "Add a room to this venue",
    venueCreated: "Venue created successfully",
    venueUpdated: "Venue updated",
    venueDeleted: "Venue deleted",
    roomCreated: "Room created successfully",
    roomUpdated: "Room updated",
    roomDeleted: "Room deleted",
    eventLocations: "Event Locations",
    addLocation: "Add Location",
    locationLabel: "Location Label",
    manageVenues: "Manage Venues",
    fullAddress: "Full Address",
    city: "City",
    state: "State",
    zipCode: "ZIP Code",
    country: "Country",
    contactName: "Contact Name",
    contactPhone: "Contact Phone",
    contactEmail: "Contact Email",
    description: "Description",
    totalCapacity: "Total Capacity",
    
    // Room Availability
    available: "Available",
    unavailable: "Unavailable",
    booked: "Booked",
    conflict: "Conflict",
    roomAvailability: "Room Availability",
    checkAvailability: "Check Availability",
    roomBooked: "Room is booked",
    roomAvailable: "Room is available",
    selectDate: "Select a date to check availability",
    bookedBy: "Booked by",
    bookings: "Bookings",
    noBookings: "No bookings",
  },
  es: {
    // Navigation
    dashboard: "Panel",
    systemPulse: "System Pulse",
    crm: "CRM",
    clients: "Clientes",
    events: "Eventos",
    calendar: "Calendario",
    recipes: "Recetas",
    inventory: "Inventario",
    documents: "Documentos",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    executionHub: "Execution Hub",
    
    // Common
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Crear",
    search: "Buscar",
    filter: "Filtrar",
    actions: "Acciones",
    status: "Estado",
    name: "Nombre",
    email: "Correo",
    phone: "Teléfono",
    address: "Dirección",
    notes: "Notas",
    date: "Fecha",
    total: "Total",
    loading: "Cargando...",
    noResults: "Sin resultados",
    duplicate: "Duplicado",
    booking: "Reserva",
    viewAll: "Ver todo",
    add: "Agregar",
    record: "Registrar",
    
    // Status labels
    planning: "Planificación",
    inProgress: "En Progreso",
    blocked: "Bloqueado",
    completed: "Completado",
    cancelled: "Cancelado",
    confirmed: "Confirmado",
    live: "Activo",
    archived: "Archivado",
    pending: "Pendiente",
    resolved: "Resuelto",
    active: "Activo",
    inactive: "Inactivo",
    
    // Execution Hub
    executionGoals: "Metas de Ejecución",
    newGoal: "Nueva Meta",
    createGoal: "Crear Meta",
    goalTitle: "Título",
    goalObjective: "Objetivo",
    targetDate: "Fecha Objetivo",
    impactAreas: "Áreas de Impacto",
    context: "Contexto",
    organizational: "Organizacional",
    event: "Evento",
    department: "Departamento",
    personal: "Personal",
    cost: "Costo",
    quality: "Calidad",
    speed: "Velocidad",
    risk: "Riesgo",
    tasks: "Tareas",
    decisions: "Decisiones",
    dependencies: "Dependencias",
    decisionLog: "Registro de Decisiones",
    newTask: "Nueva Tarea",
    createTask: "Crear Tarea",
    taskTitle: "Título de la tarea",
    taskDescription: "Descripción",
    dueDate: "Fecha Límite",
    recordDecision: "Registrar Decisión",
    decisionContent: "Decisión",
    rationale: "Razón",
    newDependency: "Nueva Dependencia",
    addDependency: "Agregar Dependencia",
    goalNotes: "Notas de la Meta",
    taskNotes: "Notas de la tarea",
    addNote: "Agregar nota...",
    noTasksYet: "No hay tareas aún",
    noDecisionsRecorded: "No hay decisiones registradas",
    noDependencies: "No hay dependencias",
    noNotesYet: "No hay notas aún",
    noGoalsYet: "No hay metas",
    createFirstGoal: "Crea tu primera meta de ejecución para comenzar",
    goalsThisMonth: "metas este mes",
    activeGoals: "Metas Activas",
    blockedGoals: "Bloqueadas",
    completedGoals: "Completadas",
    thisMonth: "Este Mes",
    searchGoals: "Buscar metas...",
    filterByStatus: "Filtrar por estado",
    all: "Todos",
    changeStatus: "Cambiar Estado",
    deleteGoal: "Eliminar Meta",
    confirmDeleteGoal: "¿Estás seguro de eliminar esta meta?",
    goalDeleted: "Meta eliminada",
    goalCreated: "Meta creada exitosamente",
    taskCreated: "Tarea creada",
    taskUpdated: "Tarea actualizada",
    decisionRecorded: "Decisión registrada",
    dependencyAdded: "Dependencia agregada",
    dependencyUpdated: "Dependencia actualizada",
    noteAdded: "Nota agregada",
    statusUpdated: "Estado actualizado",
    titleObjectiveRequired: "Título y objetivo son requeridos",
    errorLoadingGoals: "Error al cargar las metas",
    errorLoadingGoal: "Error al cargar la meta",
    errorCreatingGoal: "Error al crear la meta",
    errorCreatingTask: "Error al crear tarea",
    errorUpdating: "Error al actualizar",
    errorDeleting: "Error al eliminar",
    errorAddingNote: "Error al agregar nota",
    errorRecordingDecision: "Error al registrar decisión",
    errorAddingDependency: "Error al agregar dependencia",
    newExecutionGoal: "Nueva Meta de Ejecución",
    defineNewGoal: "Define una nueva meta operativa para tu organización",
    manageGoalsTrackProgress: "Gestiona tus metas operativas y rastrea el progreso",
    assignedGoalsAndTasks: "Metas y tareas asignadas",
    myPending: "Mis Pendientes",
    myGoals: "Mis Metas",
    assignedTasks: "Tareas Asignadas",
    
    // System Pulse / Signals
    executionSignals: "Señales de Ejecución",
    viewAllGoals: "Ver todas las metas",
    goalBlocked: "Meta Bloqueada",
    goalAtRisk: "Meta en Riesgo",
    goalCompleted: "Meta Completada",
    isBlocked: "está bloqueada",
    hasUpcomingDeadline: "tiene fecha límite próxima",
    wasCompletedThisWeek: "fue completada esta semana",
    atRisk: "en riesgo",
    completedThisWeek: "completadas esta semana",
    
    // Auth
    login: "Iniciar Sesión",
    register: "Registrarse",
    password: "Contraseña",
    confirmPassword: "Confirmar Contraseña",
    companyName: "Nombre de la Empresa",
    welcomeBack: "Bienvenido de nuevo",
    createAccount: "Crea tu cuenta",
    continueWithGoogle: "Continuar con Google",
    orContinueWith: "o continuar con",
    
    // Dashboard
    welcomeDashboard: "Bienvenido a tu panel",
    totalClients: "Total Clientes",
    upcomingEvents: "Próximos Eventos",
    totalRecipes: "Total Recetas",
    lowStockAlerts: "Alertas Stock Bajo",
    recentEvents: "Eventos Recientes",
    salesPipeline: "Pipeline de Ventas",
    
    // CRM
    addClient: "Agregar Cliente",
    clientDetails: "Detalles del Cliente",
    pipelineStage: "Etapa del Pipeline",
    totalRevenue: "Ingresos Totales",
    eventsCount: "Eventos",
    lead: "Prospecto",
    contacted: "Contactado",
    proposal: "Propuesta",
    negotiation: "Negociación",
    won: "Ganado",
    lost: "Perdido",
    
    // Events
    addEvent: "Agregar Evento",
    eventDetails: "Detalles del Evento",
    eventType: "Tipo de Evento",
    eventDate: "Fecha del Evento",
    venue: "Lugar",
    guestCount: "Número de Invitados",
    budget: "Presupuesto",
    timeline: "Cronograma",
    menu: "Menú",
    
    // Recipes
    addRecipe: "Agregar Receta",
    recipeDetails: "Detalles de Receta",
    category: "Categoría",
    servings: "Porciones",
    prepTime: "Tiempo de Preparación",
    cookTime: "Tiempo de Cocción",
    ingredients: "Ingredientes",
    instructions: "Instrucciones",
    totalCost: "Costo Total",
    costPerServing: "Costo por Porción",
    aiAssistant: "Asistente IA",
    analyzeRecipe: "Analizar Receta",
    
    // Inventory
    addIngredient: "Agregar Ingrediente",
    ingredientDetails: "Detalles del Ingrediente",
    unit: "Unidad",
    costPerUnit: "Costo por Unidad",
    stockQuantity: "Cantidad en Stock",
    minStockLevel: "Nivel Mínimo de Stock",
    supplier: "Proveedor",
    suppliers: "Proveedores",
    addSupplier: "Agregar Proveedor",
    lowStock: "Stock Bajo",
    
    // Documents
    createDocument: "Crear Documento",
    quote: "Cotización",
    beo: "Orden de Evento de Banquete",
    kitchenSheet: "Hoja de Cocina",
    packingList: "Lista de Empaque",
    miseEnPlace: "Mise en Place",
    documentType: "Tipo de Documento",
    sign: "Firmar",
    signed: "Firmado",
    draft: "Borrador",
    sent: "Enviado",
    notSigned: "Sin Firmar",
    contract: "Contrato",
    invoice: "Factura",
    
    // Settings
    companySettings: "Configuración de Empresa",
    branding: "Marca",
    templates: "Plantillas",
    documentTemplates: "Plantillas de Documentos",
    users: "Usuarios",
    language: "Idioma",
    currency: "Moneda",
    primaryColor: "Color Primario",
    secondaryColor: "Color Secundario",
    logoUrl: "URL del Logo",
    
    // Portal
    clientPortal: "Portal del Cliente",
    signDocument: "Firmar Documento",
    thankYou: "¡Gracias por su firma!",
    
    // Locations & Venues
    locations: "Ubicaciones",
    venues: "Venues",
    rooms: "Salones",
    addVenue: "Agregar Venue",
    editVenue: "Editar Venue",
    addRoom: "Agregar Salón",
    editRoom: "Editar Salón",
    venueName: "Nombre del Venue",
    roomName: "Nombre del Salón",
    capacity: "Capacidad",
    floor: "Piso",
    amenities: "Amenidades",
    setupOptions: "Opciones de Montaje",
    hourlyRate: "Tarifa por Hora",
    parkingInfo: "Información de Estacionamiento",
    accessibilityInfo: "Accesibilidad",
    isPrimary: "Ubicación Principal",
    savedAddresses: "Direcciones Guardadas",
    addAddress: "Agregar Dirección",
    locationType: "Tipo de Ubicación",
    onsite: "En sitio",
    offsite: "Fuera de sitio",
    clientAddress: "Dirección del Cliente",
    multiLocation: "Múltiples Ubicaciones",
    selectVenue: "Seleccionar Venue",
    selectRoom: "Seleccionar Salón",
    arrivalTime: "Hora de Llegada",
    departureTime: "Hora de Salida",
    setupNotes: "Notas de Montaje",
    parkingInstructions: "Instrucciones de Estacionamiento",
    noVenuesYet: "No hay venues aún",
    createFirstVenue: "Crea tu primer venue para comenzar",
    noRoomsYet: "No hay salones en este venue",
    addFirstRoom: "Agrega un salón a este venue",
    venueCreated: "Venue creado exitosamente",
    venueUpdated: "Venue actualizado",
    venueDeleted: "Venue eliminado",
    roomCreated: "Salón creado exitosamente",
    roomUpdated: "Salón actualizado",
    roomDeleted: "Salón eliminado",
    eventLocations: "Ubicaciones del Evento",
    addLocation: "Agregar Ubicación",
    locationLabel: "Etiqueta de Ubicación",
    manageVenues: "Gestionar Venues",
    fullAddress: "Dirección Completa",
    city: "Ciudad",
    state: "Provincia/Estado",
    zipCode: "Código Postal",
    country: "País",
    contactName: "Nombre de Contacto",
    contactPhone: "Teléfono de Contacto",
    contactEmail: "Email de Contacto",
    description: "Descripción",
    totalCapacity: "Capacidad Total",
    
    // Room Availability
    available: "Disponible",
    unavailable: "No Disponible",
    booked: "Reservado",
    conflict: "Conflicto",
    roomAvailability: "Disponibilidad del Salón",
    checkAvailability: "Verificar Disponibilidad",
    roomBooked: "Salón reservado",
    roomAvailable: "Salón disponible",
    selectDate: "Selecciona una fecha para verificar disponibilidad",
    bookedBy: "Reservado por",
    bookings: "Reservas",
    noBookings: "Sin reservas",
  },
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("eep_language");
    return saved || "en";
  });

  const t = useCallback(
    (key) => {
      return translations[language]?.[key] || translations.en[key] || key;
    },
    [language]
  );

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem("eep_language", lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
